import { AlertTriangle, ChevronDown, ChevronUp, User } from 'lucide-react';
import { useState } from 'react';

import {
  PREVIEW_BLOCK_ITEM_TYPE,
  type ExcludeGroup,
  type IncludeGroup,
  type PreviewBlockItem,
} from '../../types/types';
import { validateLogic } from '../../utils/validationLogic';

import { Alert, AlertDescription } from '@/shared/components/shadcn/alert';
import { Button } from '@/shared/components/shadcn/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/shadcn/collapsible';

type Props = {
  includeGroups: IncludeGroup[];
  excludeGroup: ExcludeGroup;
};

export function LogicPreview(props: Props) {
  const [isLogicPreviewOpen, setIsLogicPreviewOpen] = useState(true);

  const validation = validateLogic(props.includeGroups, props.excludeGroup);

  const renderPreviewItem = (item: PreviewBlockItem, index: number) => {
    switch (item.type) {
      case PREVIEW_BLOCK_ITEM_TYPE.NOT:
        return (
          <span
            key={index}
            className="mx-1 rounded bg-red-50 px-1 py-0.5 text-xs font-semibold text-red-600"
          >
            {item.value}
          </span>
        );
      case PREVIEW_BLOCK_ITEM_TYPE.OR:
        return (
          <span
            key={index}
            className="mx-1 rounded bg-red-50 px-1 py-0.5 text-xs font-semibold text-green-600"
          >
            {item.value}
          </span>
        );
      case PREVIEW_BLOCK_ITEM_TYPE.AND:
        return (
          <span
            key={index}
            className="mx-1 rounded bg-blue-50 px-1 py-0.5 text-xs font-semibold text-blue-600"
          >
            {item.value}
          </span>
        );
      case PREVIEW_BLOCK_ITEM_TYPE.EXPRESSION:
        return (
          <span key={index} className="mx-0.5 font-bold text-gray-500">
            {item.value}
          </span>
        );
      case PREVIEW_BLOCK_ITEM_TYPE.KEYWORD:
        return (
          <span
            key={index}
            className="mx-0.5 rounded bg-gray-100 px-1 py-0.5 text-xs text-[12px] font-semibold text-gray-800"
          >
            &quot;{item.value}&quot;
          </span>
        );
      case PREVIEW_BLOCK_ITEM_TYPE.USERNAME:
        return (
          <span
            key={index}
            className="mx-0.5 rounded px-1 py-0.5 text-xs text-[12px] font-semibold text-gray-800"
          >
            <User className="-translate-y-0.4 mr-1 inline-block size-4" />
            {item.value}
          </span>
        );
      default:
        return null;
    }
  };

  const renderLogicPreview = (previewItems: PreviewBlockItem[]) => {
    if (previewItems.length === 0) {
      return <span className="text-gray-400 italic">No conditions defined</span>;
    }

    return (
      <span className="break-words">
        {previewItems.map((item, index) => renderPreviewItem(item, index))}
      </span>
    );
  };
  return (
    <div className="rounded-lg bg-gray-50">
      <div className="px-4 py-3">
        <Collapsible open={isLogicPreviewOpen} onOpenChange={setIsLogicPreviewOpen}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="flex h-auto w-full items-center justify-between p-0 hover:bg-transparent"
            >
              <span className="text-sm font-medium text-gray-700">Logic Preview</span>
              {isLogicPreviewOpen ? (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              )}
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-3 space-y-3">
            <div
              className="max-h-32 min-h-[32px] overflow-y-auto rounded-lg p-4 font-mono text-sm leading-relaxed"
              style={{
                fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              }}
            >
              {renderLogicPreview(validation.logicPreview)}
            </div>

            {/* Quick legend */}
          </CollapsibleContent>
        </Collapsible>

        {validation.warnings.length > 0 && (
          <Alert className="mt-3 flex items-center">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="mt-1">
              <ul className="space-y-1">
                {validation.warnings.map((warning, index) => (
                  <li key={index} className="text-sm">
                    {warning}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}
